<template>
  <BForm novalidate>
    <FormWrapper :validator="$v.form">
      <BaseCertificationFormStepHeader />
      <BaseCertificationFormSteps />
      <component :is="stepComponent" v-model="form" :v="$v"></component>
      <BaseCertificationFormStepFooter
        @form:submit="submit"
        @form:draft="draft"
      />
      <BaseCertificationFormStatus />
    </FormWrapper>
  </BForm>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import BaseForm from "../Form/BaseForm";
import BaseCertificationFormStep from "./BaseCertificationFormStep";
import BaseCertificationFormStepHeader from "./BaseCertificationFormStepHeader";
import BaseCertificationFormStepFooter from "./BaseCertificationFormStepFooter";
import BaseCertificationFormSteps from "./BaseCertificationFormSteps";
import BaseCertificationFormStatus from "./BaseCertificationFormStatus";
import BaseAdminCertificationSummary from "./BaseAdminCertificationSummary";
import BaseUserCertificationSummary from "./BaseUserCertificationSummary";

import ModelFactory from "@/model/ModelFactory";
import EventApi from "@/api/Event";

import CertificationFormGroup from "./BaseCertificationFormGroup";

import Vue from "vue";
Vue.component("CertificationFormGroup", CertificationFormGroup);

import mapValues from "lodash/mapValues";
import pick from "lodash/pick";
import values from "lodash/values";
import get from "lodash/get";
import { getBase64 } from "@/helpers/FileBase64";

const _ = { mapValues, pick, values, get };

export default {
  name: "BaseCertificationForm",
  components: {
    BaseCertificationFormStep,
    BaseCertificationFormStepHeader,
    BaseCertificationFormStepFooter,
    BaseCertificationFormSteps,
    BaseCertificationFormStatus,
    BaseAdminCertificationSummary,
    BaseUserCertificationSummary
  },
  extends: BaseForm,
  props: {
    value: {
      type: Object,
      default: () => ModelFactory.create("EventModel")
    },
    moderationDisabled: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: "edit"
    }
  },
  data() {
    return {
      isLoaded: false,
      version: "base",
      steps: [],
      stepComponentPrefix: "BaseCertificationFormStep",
      form: this.value,
      formUrl: EventApi.getEventUrl(),
      history: []
    };
  },
  computed: {
    ...mapState("Form", ["currentStep"]),
    ...mapState("User", ["user", "organizations"]),
    ...mapGetters("User", ["hasActiveOrganization", "isAdmin", "isUser"]),
    stepComponent() {
      const componentName = this.stepComponentPrefix + this.currentStep;
      return this.$options.components[componentName]
        ? componentName
        : this.stepComponentPrefix;
    },
    canCreate() {
      return (!this.isAdmin && this.hasActiveOrganization) || this.isAdmin;
    },
    showSummary() {
      return this.isAdmin ||
        this[this.formName].approved ||
        this[this.formName].status === "edit"
        ? true
        : false;
    }
  },
  watch: {
    value: {
      handler(newValue, oldValue) {
        if (newValue.id !== oldValue.id) {
          this.form = newValue;
        }
      },
      deep: true
    },
    "form.eventName": function(newValue, oldValue) {
      if (newValue && newValue != oldValue) {
        this.$store.commit(
          "App/SET_PAGE_TITLE",
          newValue.length >= 3 ? newValue : "Formularz weryfikacji wydarzeń"
        );
      }
    },
    isSent(newValue) {
      if (newValue === true && this.isAdmin) {
        this.$router.push("/certification");
      }
    }
  },

  mounted() {
    this.loading();

    Promise.all([
      this.$store.dispatch("Form/setSteps", this.steps)
      // this.$store.dispatch("User/getCurrent")
    ]).then(() => {
      if (this.$route.params.id) {
        this.loadForm(this.$route.params.id).then(this.loaded);
      } else {
        this.newForm().then(this.loaded);
      }
    });
  },

  destroyed() {
    this.$store.dispatch("Event/unload");
  },

  methods: {
    getBase64,

    async getFormData() {
      let data = Object.assign({}, this[this.formName]);
      let newFiles = [];
      for (let key in data.files) {
        if (data.files.hasOwnProperty(key)) {
          if (data.files[key] && data.files[key].name) {
            let file = ModelFactory.create("FileModel");
            file.filename = data.files[key].name;
            file.type = key;
            file.data = await this.getBase64(data.files[key]);
            newFiles.push(file);
          }
        }
      }
      data.files = newFiles;

      let comments = this.$store.state.Event.comments;

      data.comments = _.values(
        _.mapValues(comments, function(item) {
          const comment = _.pick(item, [
            "mark",
            "comment",
            "autoCompliance",
            "name"
          ]);
          return comment;
        })
      ).map(comment => {
        if (
          comment.mark === true &&
          _.get(data, comment.name.split(".")) === null
        ) {
          comment.mark = false;
        }
        return comment;
      }); //.filter(item => item.mark === true || item.mark === null);

      data.publicComments = (
        this.$store.state.Event.publicComments || ""
      ).replace(/\n/g, "<br/>");
      data.staffComments = (
        this.$store.state.Event.staffComments || ""
      ).replace(/\n/g, "<br/>");

      return data;
    },

    afterSubmit(res) {
      if (res && res.data && res.data.success) {
        let event = ModelFactory.create("EventModel", res.data.object);
        this[this.formName] = event;
        this.$store.commit("Event/SET_EVENT", event);
      }
    },

    // getAxiosOptions() {
    //   return {
    //     data: null,
    //     method: this[this.formName].id ? "put" : "post",
    //     responseType: "json",
    //     withCredentials: true,
    //     headers: {
    //       "Content-Type": "multipart/form-data"
    //     },
    //     url: this.formUrl
    //   };
    // },

    isSubmitDisabled() {
      return this.disabled && !this.isAdmin;
    },

    reset() {
      this.$v && this.$v[this.formName].$reset();
      this.$store.dispatch("Form/resetErrors");
      this.$store.commit("Form/SET_DISABLED", this.disabled);

      this.$store.commit(
        "Event/SET_MODERATION_DISABLED",
        this.moderationDisabled
      );
    },

    newForm() {
      this.form = ModelFactory.create("EventModel");
      this.form.name = this.user.fullname;
      this.form.email = this.user.email;
      this.form.phone = this.user.phone;
      if (this.user.organizations.length === 1) {
        this.form.applicant = ModelFactory.create("OrganizationModel", {
          ...this.user.organizations[0]
        });
      }
      return new Promise(resolve => resolve(true));
    },

    async loadForm(eventId) {
      let mode = this.mode;

      let form = await this.$store.dispatch("Event/getWithHistory", {
        eventId,
        mode
      });
      if (form.id) {
        this.form = ModelFactory.create("EventModel", form);
        return new Promise(resolve => resolve(true));
      } else {
        this.$router.push("/404");
      }
    },

    loading() {
      this.$store.dispatch("App/loading", "certification");
    },

    loaded() {
      this.$store.dispatch("App/loaded", "certification");
      this.isLoaded = true;
    }
  }
};

/*
mounted() {

  },

  methods: {
    loadForm(id) {
      .then(res => {
        if (!res.id) {
          this.$router.push("/404");
        }
        this.form = ModelFactory.create("EventModel", res);
      });
    }
  }
  */
</script>
